import React from 'react';
import { StagingPriority } from 'data/pps/mockData';

interface PrintableTableProps {
  data: StagingPriority[];
  dateTime: string;
}

const PrintableTable: React.FC<PrintableTableProps> = ({ data, dateTime }) => {
  // Function to parse job start time (format: "3-17-25 10:35 AM")
  const parseJobStartTime = (timeString: string): Date | null => {
    try {
      // Convert "3-17-25 10:35 AM" to a format JavaScript can parse
      const [datePart, timePart, ampm] = timeString.split(' ');
      const [month, day, year] = datePart.split('-');

      // Add 2000 to year if it's 2-digit
      const fullYear = year.length === 2 ? `20${year}` : year;

      // Create a proper date string: "MM/DD/YYYY HH:MM AM/PM"
      const formattedDateString = `${month}/${day}/${fullYear} ${timePart} ${ampm}`;

      return new Date(formattedDateString);
    } catch (error) {
      return null;
    }
  };

  // Function to calculate background color based on job start time
  const getRowBackgroundColor = (jobStartTime: string, index: number): string => {
    if (!jobStartTime) {
      // Default alternating colors if no job start time
      return index % 2 === 0 ? '#ffffff' : '#f9fafb';
    }

    const jobDate = parseJobStartTime(jobStartTime);
    if (!jobDate || isNaN(jobDate.getTime())) {
      // Fallback to default colors if parsing fails
      return index % 2 === 0 ? '#ffffff' : '#f9fafb';
    }

    const currentTime = new Date();

    // Calculate time difference in hours
    const timeDifferenceHours = (jobDate.getTime() - currentTime.getTime()) / (1000 * 60 * 60);

    // Apply color conditions
    if (timeDifferenceHours < 3) {
      return 'oklch(80.8% 0.114 19.571)'; // Light red background
    } else if (timeDifferenceHours < 4) {
      return 'oklch(83.7% 0.128 66.29)'; // Light orange background
    } else {
      // Default alternating colors
      return index % 2 === 0 ? '#ffffff' : '#f9fafb';
    }
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}
      >
        <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>
          <span style={{ fontWeight: '700', color: '#4d2e6d' }}>Date: </span>
          {dateTime}
        </div>
      </div>

      {/* Table */}
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '14px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          borderRadius: '8px',
          overflow: 'hidden',
          tableLayout: 'fixed'
        }}
      >
        {/* Table Header */}
        <thead>
          <tr>
            <th
              style={{
                backgroundColor: '#f9fafb',
                color: '#374151',
                fontWeight: '700',
                fontSize: '15px',
                borderBottom: '1px solid #e5e7eb',
                padding: '20px 16px',
                textAlign: 'left',
                width: '5%'
              }}
            >
              #
            </th>
            <th
              style={{
                backgroundColor: '#f9fafb',
                color: '#374151',
                fontWeight: '700',
                fontSize: '15px',
                borderBottom: '1px solid #e5e7eb',
                padding: '20px 16px',
                textAlign: 'left',
                width: '12%'
              }}
            >
              Jobs
            </th>
            <th
              style={{
                backgroundColor: '#f9fafb',
                color: '#374151',
                fontWeight: '700',
                fontSize: '15px',
                borderBottom: '1px solid #e5e7eb',
                padding: '20px 16px',
                textAlign: 'left',
                width: '12%'
              }}
            >
              Room
            </th>
            <th
              style={{
                backgroundColor: '#f9fafb',
                color: '#374151',
                fontWeight: '700',
                fontSize: '15px',
                borderBottom: '1px solid #e5e7eb',
                padding: '20px 16px',
                textAlign: 'left',
                width: '15%'
              }}
            >
              Description
            </th>
            <th
              style={{
                backgroundColor: '#f9fafb',
                color: '#374151',
                fontWeight: '700',
                fontSize: '15px',
                borderBottom: '1px solid #e5e7eb',
                padding: '20px 16px',
                textAlign: 'left',
                width: '12%'
              }}
            >
              Stage By
            </th>
            <th
              style={{
                backgroundColor: '#f9fafb',
                color: '#374151',
                fontWeight: '700',
                fontSize: '15px',
                borderBottom: '1px solid #e5e7eb',
                padding: '20px 16px',
                textAlign: 'left',
                width: '10%'
              }}
            >
              Job Start Time
            </th>
            <th
              style={{
                backgroundColor: '#f9fafb',
                color: '#374151',
                fontWeight: '700',
                fontSize: '15px',
                borderBottom: '1px solid #e5e7eb',
                padding: '20px 16px',
                textAlign: 'left',
                width: '10%'
              }}
            >
              Job Create / Edit Time
            </th>
            <th
              style={{
                backgroundColor: '#f9fafb',
                color: '#374151',
                fontWeight: '700',
                fontSize: '15px',
                borderBottom: '1px solid #e5e7eb',
                padding: '20px 16px',
                textAlign: 'left',
                width: '8%'
              }}
            >
              Status
            </th>
            <th
              style={{
                backgroundColor: '#f9fafb',
                color: '#374151',
                fontWeight: '700',
                fontSize: '15px',
                borderBottom: '1px solid #e5e7eb',
                padding: '20px 16px',
                textAlign: 'left',
                width: '16%'
              }}
            >
              Notes
            </th>
          </tr>
        </thead>

        {/* Table Body */}
        <tbody>
          {data.map((row, index) => (
            <tr
              key={index}
              style={{
                backgroundColor: getRowBackgroundColor(row.jobStartTime, index)
              }}
            >
              <td
                style={{
                  border: '1px solid #e5e7eb',
                  borderTop: 'none',
                  padding: '16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  textAlign: 'center',
                  wordWrap: 'break-word'
                }}
              >
                {index + 1}
              </td>
              <td
                style={{
                  border: '1px solid #e5e7eb',
                  borderTop: 'none',
                  padding: '16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  wordWrap: 'break-word'
                }}
              >
                {row.jobs}
              </td>
              <td
                style={{
                  border: '1px solid #e5e7eb',
                  borderTop: 'none',
                  padding: '16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  wordWrap: 'break-word'
                }}
              >
                {row.room}
              </td>
              <td
                style={{
                  border: '1px solid #e5e7eb',
                  borderTop: 'none',
                  padding: '16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  wordWrap: 'break-word'
                }}
              >
                {row.description || '-'}
              </td>
              <td
                style={{
                  border: '1px solid #e5e7eb',
                  borderTop: 'none',
                  padding: '16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  wordWrap: 'break-word'
                }}
              >
                {row.stageBy}
              </td>
              <td
                style={{
                  border: '1px solid #e5e7eb',
                  borderTop: 'none',
                  padding: '16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  wordWrap: 'break-word'
                }}
              >
                {row.jobStartTime}
              </td>
              <td
                style={{
                  border: '1px solid #e5e7eb',
                  borderTop: 'none',
                  padding: '16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  wordWrap: 'break-word'
                }}
              >
                {row.createdAt}
              </td>
              <td
                style={{
                  border: '1px solid #e5e7eb',
                  borderTop: 'none',
                  padding: '16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  wordWrap: 'break-word'
                }}
              >
                {row.status}
              </td>
              <td
                style={{
                  border: '1px solid #e5e7eb',
                  borderTop: 'none',
                  padding: '16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word'
                }}
              >
                {(() => {
                  if (!row.notes || !Array.isArray(row.notes) || row.notes.length === 0) {
                    return '-';
                  }
                  // Join array of notes with "/"
                  return row.notes.join('/');
                })()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <style>
        {`
                    * {
                        box-sizing: border-box;
                    }
                    @media print {
                        @page { 
                            size: auto; 
                            margin: 10mm; 
                        }
                        body { 
                            background: white !important; 
                            font-family: Arial, sans-serif !important;
                        }
                        table {
                            font-size: 13px !important;
                            table-layout: fixed !important;
                            width: 100% !important;
                            border-collapse: collapse !important;
                        }
                        th {
                            text-align: center !important;
                            font-size: 10px !important;
                            padding: 12px 8px !important;
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                            word-wrap: break-word !important;
                        }
                        td {
                            font-size: 10px !important;
                            text-align: center !important;
                            padding: 10px 8px !important;
                            line-height: 1.3 !important;
                            border-collapse: collapse !important;
                            word-wrap: break-word !important;
                            overflow-wrap: break-word !important;
                        }
                        tr {
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                            page-break-inside: avoid !important;
                        }
                        /* Edge browser specific fixes */
                        @supports (-ms-ime-align: auto) {
                            table {
                                width: 100% !important;
                                table-layout: fixed !important;
                            }
                        }
                        /* Firefox specific fixes */
                        @-moz-document url-prefix() {
                            table {
                                border-collapse: separate !important;
                                border-spacing: 0 !important;
                            }
                        }
                    }
                `}
      </style>
    </div>
  );
};

export default PrintableTable;
